import React, { useState, useEffect } from 'react';
import SearchFieldsSelect, { useSearchFields, getSearchFieldsPlaceholder } from '../../Components/SearchFieldsSelect/SearchFieldsSelect';
import API from '../../API/API';
import { 
  Table, 
  Tag, 
  Space, 
  Typography, 
  Avatar, 
  Tooltip, 
  Input, 
  Button, 
  Row, 
  Col,
  Badge,
  Spin,
  Select,
  DatePicker,
  message,
  Modal,
  Divider,
  Card
} from 'antd';
import { 
  CarOutlined, 
  UserOutlined, 
  MailOutlined,
  PhoneOutlined,
  NumberOutlined,
  TagOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  LoadingOutlined,
  EditOutlined,
  CalendarOutlined,
  TeamOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  CheckOutlined,
  FileTextOutlined,
  CloseOutlined
} from '@ant-design/icons';
import Logo from "../../JPEG/Logo.png";
import LeadDetailsDrawer from './LeadDetailsDrawer';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ZohoSign = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  // The search a request uses: the box's text, set 0.5 s after typing pauses
  // (at once when the box is cleared), so a search is not sent per keystroke.
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const trimmed = searchText.trim();
    if (!trimmed) {
      setSearchQuery('');
      return undefined;
    }
    const timer = setTimeout(() => setSearchQuery(trimmed), 500);
    return () => clearTimeout(timer);
  }, [searchText]);
  const [searchFields, setSearchFields] = useSearchFields('zoho-sign:search-fields');
  // The "Search in" group only changes the results of a search, so changing it
  // with an empty search box sends no request.
  const effectiveSearchFields = searchQuery ? searchFields : null;
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [editingStatus, setEditingStatus] = useState({ 
    rowId: null, 
    field: null,
    value: '',
    loading: false 
  });
  
  // Agent filter state
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const userData = JSON.parse(localStorage.getItem("userData"));
  const isAdmin = userData?.role === "ADMIN";
  const [serviceAgentsMap, setServiceAgentsMap] = useState({});
  const [loadingServiceAgents, setLoadingServiceAgents] = useState(false);
  const [selectedServiceAgentId, setSelectedServiceAgentId] = useState(null);
   
  // Date filter states
  const [dateFilterType, setDateFilterType] = useState("This Month");
  const [customDateRange, setCustomDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month")
  ]);
  
  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedLeadData, setSelectedLeadData] = useState(null);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [commentsModalVisible, setCommentsModalVisible] = useState(false);
    const [selectedLeadForComment, setSelectedLeadForComment] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [addingComment, setAddingComment] = useState(false);
const [editingDate, setEditingDate] = useState(null);

// Add these with your other state declarations
const [serviceAgentModalVisible, setServiceAgentModalVisible] = useState(false);
const [selectedLeadForServiceAgent, setSelectedLeadForServiceAgent] = useState(null);
const [selectedServiceAgentForLead, setSelectedServiceAgentForLead] = useState(null);
const [savingServiceAgent, setSavingServiceAgent] = useState(false);
const [editingSpecification, setEditingSpecification] = useState(null);
const [editingSpecValue, setEditingSpecValue] = useState('');
const [updatingSpecification, setUpdatingSpecification] = useState(false);
 

const handleUpdateSpecification = async (leadId, newSpecification) => {
    if (!leadId) return;
    
    const trimmedValue = newSpecification?.trim() || '';
    
    if (trimmedValue.length > 2000) {
        message.warning('Specification cannot exceed 2000 characters');
        return;
    }
    
    setUpdatingSpecification(true);
    try {
        // First, get the current lead data to preserve other vehicleInfo fields
        const currentLead = leads.find(lead => lead._id === leadId);
        const currentVehicleInfo = currentLead?.vehicleInfo || {};
        
        // Merge the specification with existing vehicleInfo
        const payload = {
            vehicleInfo: {
                ...currentVehicleInfo, // Preserve all existing fields
                specification: trimmedValue // Update only specification
            }
        };
        
        console.log('Sending payload with preserved data:', payload);
        
        const response = await API.UpdateLead(leadId, payload);
        
        if (response?.status) {
            message.success('Specification updated successfully!');
            fetchLeads(); // Refresh the leads list
        } else {
            message.error(response?.message || 'Failed to update specification');
        }
    } catch (error) {
        console.error('Error updating specification:', error);
        message.error('Failed to update specification');
    } finally {
        setUpdatingSpecification(false);
        setEditingSpecification(null);
        setEditingSpecValue('');
    }
};
// Handle opening the service agent selection modal
const handleServiceAgentEdit = (record) => {
    const currentServiceAgentId = record.zohoServiceHandledBy?._id || record.zohoServiceHandledBy;
    setSelectedLeadForServiceAgent(record);
    setSelectedServiceAgentForLead(currentServiceAgentId || null);
    setServiceAgentModalVisible(true);
};

// Handle saving the service agent
const handleServiceAgentSave = async () => {
    if (!selectedLeadForServiceAgent) return;
    
    setSavingServiceAgent(true);
    
    try {
        const payload = {
            zohoServiceHandledBy: selectedServiceAgentForLead || null // Set to null if unassigned
        };
        
        const response = await API.UpdateLead(selectedLeadForServiceAgent._id, payload);
        
        if (response?.status || response?.success) {
            message.success(selectedServiceAgentForLead ? 'Service agent assigned successfully!' : 'Service agent unassigned successfully!');
            
            // Update local state
            setLeads(prevLeads => 
                prevLeads.map(lead => 
                    lead._id === selectedLeadForServiceAgent._id ? { 
                        ...lead, 
                        zohoServiceHandledBy: selectedServiceAgentForLead
                    } : lead
                )
            );
            
            // Close modal
            setServiceAgentModalVisible(false);
            setSelectedLeadForServiceAgent(null);
            setSelectedServiceAgentForLead(null);
            
            // Refresh leads to get populated agent data
            fetchLeads();
            fetchServiceAgents(); // Refresh the agents map
        } else {
            message.error(response?.message || 'Failed to assign service agent');
        }
    } catch (error) {
        console.error('Error assigning service agent:', error);
        message.error('Failed to save service agent assignment');
    } finally {
        setSavingServiceAgent(false);
    }
};
    const handleAddComment = async () => {
    if (!newComment.trim()) {
        message.warning('Please enter a comment');
        return;
    }

    if (!selectedLeadForComment) return;

    setAddingComment(true);
    try {
        const existingComments = selectedLeadForComment.zohoPageComment || [];
        const payload = {
            zohoPageComment: [
                ...existingComments,
                {
                    comment: newComment.trim(),
                    changedAt: new Date().toISOString()
                }
            ]
        };

        const response = await API.UpdateLead(selectedLeadForComment._id, payload);

        if (response?.status) {
            message.success('Comment added successfully!');
            setNewComment('');
            setCommentsModalVisible(false);
            setSelectedLeadForComment(null);
            // Refresh the leads list
            fetchLeads();
        } else {
            message.error(response?.message || 'Failed to add comment');
        }
    } catch (error) {
        console.error('Error adding comment:', error);
        message.error('Failed to add comment');
    } finally {
        setAddingComment(false);
    }
};
const formatCommentDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
};

  // Fetch agents on component mount
  useEffect(() => {
    fetchAgents();
    fetchServiceAgents();
  }, []);
const getServiceAgentName = (record) => {
    const serviceAgentId = record.zohoServiceHandledBy?._id || record.zohoServiceHandledBy;
    if (!serviceAgentId) return null;
    
    // Check if already populated as object
    if (record.zohoServiceHandledBy && typeof record.zohoServiceHandledBy === 'object') {
        return `${record.zohoServiceHandledBy.firstName || ''} ${record.zohoServiceHandledBy.lastName || ''}`.trim() || 
               record.zohoServiceHandledBy.userName || 'Service Agent';
    }
    
    // Check in our map
    const agentInfo = serviceAgentsMap[serviceAgentId];
    return agentInfo?.name || null;
};

const getDocServiceAgentId = (record) => {
    const serviceAgent = record?.zohoServiceHandledBy;
    if (!serviceAgent) return null;
    if (typeof serviceAgent === 'object') {
        return serviceAgent._id || serviceAgent.id || serviceAgent.$oid || null;
    }
    return serviceAgent;
};

const hasDocServiceAgentAssigned = (record) => Boolean(getDocServiceAgentId(record));

const showMissingDocServiceAgentError = () => {
    message.error('Please assign the Doc Service-Agent');
};


  const fetchAgents = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      if (userData?.role === "ADMIN") {
        const response = await API.userGetAll();
        if (response?.success) {
          const salesAgents = (response.data || []).filter(user => user.role === "SALES-AGENT");
          setAgents(salesAgents);
        }
      }
    } catch (error) {
      console.error("Failed to fetch agents", error);
    }
  };
const fetchServiceAgents = async () => {
    setLoadingServiceAgents(true);
    try {
        const response = await API.userGetAll();
        if (response?.success) {
            const agentsMap = {};
            // Get all users with SERVICE-AGENT or SUPPORT-AGENT role
            const serviceAgentsList = (response.data || []).filter(
                user => user.role === "SERVICE-AGENT" || user.role === "SUPPORT-AGENT"
            );
            serviceAgentsList.forEach(agent => {
                agentsMap[agent._id] = {
                    name: `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.userName || agent.email,
                    email: agent.email,
                    phone: agent.directNumber,
                    role: agent.role
                };
            });
            setServiceAgentsMap(agentsMap);
        }
    } catch (error) {
        console.error("Failed to fetch service agents", error);
    } finally {
        setLoadingServiceAgents(false);
    }
};

  // Date filter options
  const dateFilterOptions = [
    { label: 'Today', value: 'Today' },
    { label: 'Yesterday', value: 'Yesterday' },
    { label: 'Last 7 Days', value: 'Last 7 Days' },
    { label: 'Last 30 Days', value: 'Last 30 Days' },
    { label: 'This Month', value: 'This Month' },
    { label: 'Last Month', value: 'Last Month' },
  ];

  // Handle date filter change
  const handleDateFilterChange = (value) => {
    setDateFilterType(value);
    
    if (value !== 'Custom') {
      const range = getDateRange(value);
      if (range) {
        setCustomDateRange([range.startDate, range.endDate]);
      }
    }
  };

  const getDateRange = (filterType) => {
    const today = dayjs().startOf('day');
    let startDate, endDate;

    switch(filterType) {
      case 'Today':
        startDate = today;
        endDate = today.endOf('day');
        break;
      case 'Yesterday':
        startDate = today.subtract(1, 'day');
        endDate = today.subtract(1, 'day').endOf('day');
        break;
      case 'Last 7 Days':
        startDate = today.subtract(7, 'day');
        endDate = today.endOf('day');
        break;
      case 'Last 30 Days':
        startDate = today.subtract(30, 'day');
        endDate = today.endOf('day');
        break;
      case 'This Month':
        startDate = today.startOf('month');
        endDate = today.endOf('month');
        break;
      case 'Last Month':
        startDate = today.subtract(1, 'month').startOf('month');
        endDate = today.subtract(1, 'month').endOf('month');
        break;
      case 'Custom':
        if (customDateRange && customDateRange[0] && customDateRange[1]) {
          return {
            startDate: customDateRange[0].startOf('day'),
            endDate: customDateRange[1].endOf('day')
          };
        }
        return null;
      default:
        return null;
    }

    return { startDate, endDate };
  };

  // Fetch leads with pagination and filters
  useEffect(() => {
    fetchLeads();
  }, [pagination.current, pagination.pageSize, filteredInfo, sortedInfo, searchQuery, effectiveSearchFields, customDateRange, selectedAgentId,selectedServiceAgentId]);

  const fetchLeads = async () => {
    setLoading(true);

    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      if (!userData?.id) {
        console.error("User authentication data not found");
        setLoading(false);
        return;
      }

      const isSearching = Boolean(searchQuery);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        zohoDocAllow: 'Yes',
        payementAllow: 'Yes',
        zohoStatus: 'SENT,NOT-SENT,INCOMPLETE'
      };
      
      if (isSearching) {
        params.search = searchQuery;
        params.searchFields = searchFields;
      } else {
        params.dateField = 'lastPaymentDate';
        params.startDate = dayjs(customDateRange[0]).startOf('day').toISOString();
        params.endDate = dayjs(customDateRange[1]).endOf('day').toISOString();
      }

      // Add part filter
      if (filteredInfo.part && filteredInfo.part.length > 0) {
        params.part = filteredInfo.part.join(',');
      }

      // Add Zoho Status filter
      if (filteredInfo.zohoStatus && filteredInfo.zohoStatus.length > 0) {
        params.zohoStatus = filteredInfo.zohoStatus.join(',');
      }

      // Add Agent filter (only for admin)
      if (isAdmin && selectedAgentId) {
        params.agentId = selectedAgentId;
      }

      // Add sorting
      if (sortedInfo.field) {
        params.sortBy = sortedInfo.field;
        params.order = sortedInfo.order === 'ascend' ? 'asc' : 'desc';
      }

      // The server sends only the fields this page uses
      // (LeadController LEAD_VIEW_FIELDS['zoho-sign']).
      params.view = 'zoho-sign';
      const response = await API.leadGetAll(params);

      if (response?.status && response.data) {
        const sortedLeads = [...response.data].sort((a, b) => {
          const dateA = getLastPaymentDate(a);
          const dateB = getLastPaymentDate(b);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return new Date(dateB) - new Date(dateA);
        });
        
        setLeads(sortedLeads);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.totalRecords || sortedLeads.length
        }));
      } else {
        console.error("Invalid response format:", response);
        setLeads([]);
        setPagination(prev => ({ ...prev, total: 0 }));
      }

    } catch (error) {
      console.error('Failed to fetch leads:', error);
      setLeads([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  // Fetch single lead by ID (for drawer)
  const fetchLeadById = async (leadId) => {
    setDrawerLoading(true);
    try {
      const response = await API.GetLeadById(leadId);
      
      if (response.status && response.data) {
        setSelectedLeadData(response.data);
        setDrawerVisible(true);
      } else {
        console.error('Error fetching lead details:', response.message);
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(prev => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize
    }));
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSearchText('');
    setSortedInfo({});
    setSelectedAgentId(null);
    setDateFilterType('This Month');
    setCustomDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle view button click
  const handleView = (record) => {
    setSelectedLeadId(record._id);
    fetchLeadById(record._id);
  };

  // Close drawer and clear selected data
  const onDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedLeadData(null);
    setSelectedLeadId(null);
  };

  // Handle lead update after save
  const handleLeadUpdate = (updatedLead) => {
    setLeads(prevLeads => 
      prevLeads.map(lead => 
        lead._id === updatedLead._id ? updatedLead : lead
      )
    );
  };

 // Update field with history - add date parameter
const updateFieldWithHistory = async (record, field, value, historyField, dateField, customDate = null) => {
  if (field === 'zohoStatus' && !hasDocServiceAgentAssigned(record)) {
    showMissingDocServiceAgentError();
    return false;
  }

  try {
    const currentHistory = record[historyField] || [];
    const changeDate = customDate ? new Date(customDate) : new Date();
    
    const newHistoryEntry = {
      status: value,
      changedAt: changeDate.toISOString(),
      changedBy: userData?.id || 'system',
      changedByName: userData?.firstName && userData?.lastName 
        ? `${userData.firstName} ${userData.lastName}`
        : userData?.email || 'System'
    };
    
    // Check if there's already an entry with this status
    const existingEntryIndex = currentHistory.findIndex(entry => entry.status === value);
    
    let updatedHistory;
    if (existingEntryIndex !== -1) {
      // Update existing entry's date
      updatedHistory = [...currentHistory];
      updatedHistory[existingEntryIndex] = {
        ...updatedHistory[existingEntryIndex],
        changedAt: changeDate.toISOString()
      };
    } else {
      // Add new entry
      updatedHistory = [...currentHistory, newHistoryEntry];
    }
    
    const updateData = {
      [field]: value,
      [dateField]: changeDate.toISOString(),
      [historyField]: updatedHistory
    };
    
    const res = await API.UpdateLead(record._id, updateData);
    
    if (res.status) {
      await fetchLeads();
      message.success(`${field} updated successfully`);
      return true;
    } else {
      message.error(`Failed to update ${field}`);
      return false;
    }
  } catch (error) {
    console.error(`Error updating ${field}:`, error);
    message.error(`Error updating ${field}`);
    return false;
  }
};;

  const getUniqueValues = (key) => {
    const values = leads.map(lead => {
      if (key === 'part') return lead.vehicleInfo?.part;
      return lead[key];
    });
    return [...new Set(values.filter(Boolean))];
  };

  // Get agent name from lead
  const getAgentName = (lead) => {
    if (lead.agentName) return lead.agentName;
    if (lead.handledBy?.firstName && lead.handledBy?.lastName) {
      return `${lead.handledBy.firstName} ${lead.handledBy.lastName}`;
    }
    if (lead.handledBy?.name) return lead.handledBy.name;
    return 'Unassigned';
  };

  const getLastPaymentDate = (lead) => {
    const payments = lead.advancePayments || [];
    if (!payments.length) return null;
    return [...payments].sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date || null;
  };

  const getLatestPaymentAllowEntry = (lead) => {
    const paymentAllowHistory = Array.isArray(lead?.payementAllowHistory)
      ? lead.payementAllowHistory.filter((entry) => entry?.value)
      : [];

    if (paymentAllowHistory.length > 0) {
      return [...paymentAllowHistory].sort(
        (a, b) => new Date(b.changedAt || 0) - new Date(a.changedAt || 0)
      )[0];
    }

    if (lead?.payementAllow) {
      return {
        value: lead.payementAllow,
        changedAt: lead.payementAllowUpdatedAt || null,
      };
    }

    return null;
  };

  const getLatestStatusWithDate = (lead) => {
    if (!lead.zohoStatusHistory || lead.zohoStatusHistory.length === 0) {
      return {
        status: lead.zohoStatus || 'NOT-SENT',
        date: lead.zohoStatusUpdatedAt || null,
      };
    }
    // Sort by changedAt (latest first) and get the first one
    const latest = [...lead.zohoStatusHistory].sort((a, b) => 
      new Date(b.changedAt) - new Date(a.changedAt)
    )[0];
    return { status: latest.status, date: latest.changedAt };
  };

  const getLatestZohoStatusEntry = (lead, statuses) => {
    const statusSet = new Set(statuses);
    const matchingEntries = Array.isArray(lead?.zohoStatusHistory)
      ? lead.zohoStatusHistory.filter(
          (entry) => entry?.status && statusSet.has(entry.status) && entry?.changedAt
        )
      : [];

    if (matchingEntries.length > 0) {
      return [...matchingEntries].sort(
        (a, b) => new Date(b.changedAt) - new Date(a.changedAt)
      )[0];
    }

    if (lead?.zohoStatus && statusSet.has(lead.zohoStatus) && lead?.zohoStatusUpdatedAt) {
      return {
        status: lead.zohoStatus,
        changedAt: lead.zohoStatusUpdatedAt,
      };
    }

    return null;
  };

  const enhancedColumns = [
    ...(isAdmin
      ? [
          {
            title: 'Sales-Agent',
            key: 'agent',
            width: 150,
            sorter: true,
            sortOrder: sortedInfo.field === 'agent' && sortedInfo.order,
            render: (_, record) => {
              const agentName = getAgentName(record);
              const isUnassigned = agentName === 'Unassigned';

              return (
                <Tooltip title={agentName}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Avatar
                      size={28}
                      icon={<UserOutlined />}
                      style={{
                        backgroundColor: isUnassigned ? '#d9d9d9' : '#722ed1',
                        flexShrink: 0,
                      }}
                    >
                      {!isUnassigned && agentName?.[0]?.toUpperCase()}
                    </Avatar>

                    <Text
                      ellipsis={{ tooltip: true }}
                      style={{
                        fontSize: 13,
                        color: isUnassigned ? '#8c8c8c' : '#1f2937',
                        fontStyle: isUnassigned ? 'italic' : 'normal',
                      }}
                    >
                      {agentName}
                    </Text>
                  </div>
                </Tooltip>
              );
            },
          },
        ]
      : []),
 {
    title: 'Doc Service-Agent',
    key: 'serviceAgent',
    width: 180,
    sorter: (a, b) => {
        const nameA = getServiceAgentName(a) || '';
        const nameB = getServiceAgentName(b) || '';
        return nameA.localeCompare(nameB);
    },
    render: (_, record) => {
        const serviceAgentName = getServiceAgentName(record);
        const isAssigned = !!serviceAgentName;
        
        return (
            <div 
                style={{ cursor: 'pointer' }}
                onClick={() => handleServiceAgentEdit(record)}
            >
                <Tooltip title={isAssigned ? `Click to change service agent` : 'Click to assign service agent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: isAssigned ? '#52c41a' : '#f0f0f0',
                                flexShrink: 0,
                            }}
                        >
                            {isAssigned && serviceAgentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <>
                                    <Text
                                        ellipsis={{ tooltip: true }}
                                        style={{
                                            fontSize: 13,
                                            color: '#1f2937',
                                            display: 'block',
                                            fontWeight: 500
                                        }}
                                    >
                                        {serviceAgentName}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 10 }}>
                                        Click to edit
                                    </Text>
                                </>
                            ) : (
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: '#1890ff',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    + Assign Agent
                                </Text>
                            )}
                        </div>
                        <EditOutlined 
                            style={{ 
                                fontSize: 12, 
                                color: '#1890ff',
                                opacity: 0.6
                            }} 
                        />
                    </div>
                </Tooltip>
            </div>
        );
    },
},
    {
      title: 'Customer',
      key: 'customer',
      fixed: 'left',
      width: 312,
      sorter: true,
      sortOrder: sortedInfo.field === 'customer' && sortedInfo.order,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
            <Avatar
              size={32}
              icon={<UserOutlined />}
              style={{ backgroundColor: '#1890ff', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong ellipsis style={{ display: 'block', fontSize: 14, lineHeight: 1.3 }}>
                {record.personalInfo?.fullName || 'N/A'}
              </Text>
              <Text
                type="secondary"
                ellipsis
                style={{ display: 'block', fontSize: 12, lineHeight: 1.3 }}
              >
                <MailOutlined style={{ marginRight: 4 }} />
                {record.personalInfo?.email || 'N/A'}
              </Text>
            </div>
          </div>

          <div style={{ paddingLeft: 42, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Tooltip title={record.personalInfo?.phoneNumber}>
              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {record.personalInfo?.phoneNumber || 'N/A'}
              </Text>
            </Tooltip>

            <Tag
              color={record.leadType === 'inbound' ? 'green' : 'blue'}
              style={{ fontSize: 11, marginInlineEnd: 0 }}
            >
              <TagOutlined style={{ marginRight: 4 }} />
              {record.leadType || 'N/A'}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Order #',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 150,
      sorter: true,
      sortOrder: sortedInfo.field === 'orderNumber' && sortedInfo.order,
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Tooltip title={text || 'N/A'}>
            <Tag
              color="purple"
              icon={<NumberOutlined />}
              style={{ cursor: 'pointer', marginInlineEnd: 0, whiteSpace: 'nowrap' }}
            >
              {text || 'N/A'}
            </Tag>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Vehicle',
      key: 'vehicle',
      width: 240,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
            <CarOutlined style={{ color: '#fa8c16', flexShrink: 0, marginTop: 2 }} />
            <Text strong ellipsis style={{ display: 'block', minWidth: 0, lineHeight: 1.3 }}>
              {record.vehicleInfo?.year || ''} {record.vehicleInfo?.make || ''}
            </Text>
          </div>
          <div style={{ paddingLeft: 20, minWidth: 0 }}>
            <Text type="secondary" ellipsis style={{ display: 'block', fontSize: 12, lineHeight: 1.3 }}>
              {record.vehicleInfo?.model || 'N/A'}
            </Text>
          </div>
          {record.vehicleInfo?.part && (
            <div style={{ paddingLeft: 20, marginTop: 2 }}>
              <Tag color="cyan" icon={<TagOutlined />} style={{ fontSize: 11, marginInlineEnd: 0 }}>
                {record.vehicleInfo.part}
              </Tag>
            </div>
          )}
        </div>
      ),
    },
       {
    title: 'Specification',
    key: 'specification',
    width: 250,
    render: (_, record) => {
        const specification = record.vehicleInfo?.specification || '';
        const isEditing = editingSpecification === record._id;
        
        if (isEditing) {
            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        value={editingSpecValue}
                        onChange={(e) => setEditingSpecValue(e.target.value)}
                        placeholder="Enter specification details..."
                        style={{ flex: 1, fontSize: '12px' }}
                        autoFocus
                        maxLength={2000}
                        showCount
                    />
                    <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={updatingSpecification}
                            onClick={() => handleUpdateSpecification(record._id, editingSpecValue)}
                            style={{ minWidth: '32px' }}
                        />
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setEditingSpecification(null);
                                setEditingSpecValue('');
                            }}
                        />
                    </div>
                </div>
            );
        }
        
        return (
            <div 
                style={{ 
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    transition: 'all 0.3s',
                    backgroundColor: specification ? 'transparent' : '#fafafa',
                    border: '1px solid transparent',
                    minHeight: '60px'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = specification ? 'transparent' : '#fafafa';
                    e.currentTarget.style.borderColor = 'transparent';
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setEditingSpecification(record._id);
                    setEditingSpecValue(specification);
                }}
            >
                {specification ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <FileTextOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>Specification:</Text>
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            display: 'block',
                            lineHeight: '1.5'
                        }}>
                            {specification.length > 100 
                                ? `${specification.substring(0, 100)}...` 
                                : specification}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <EditOutlined style={{ marginRight: '4px' }} />
                                Click to edit
                            </Text>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <FileTextOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
                            <EditOutlined style={{ marginRight: '4px', fontSize: '10px' }} />
                            Click to add specification
                        </Text>
                    </div>
                )}
            </div>
        );
    },
}, 
  {
  title: 'Zoho Status',
  key: 'zohoStatus',
  width: 260,
  filters: [
    { text: 'NOT-SENT', value: 'NOT-SENT' },
    { text: 'SENT', value: 'SENT' },
    { text: 'INCOMPLETE', value: 'INCOMPLETE' },
  ],
  filteredValue: filteredInfo.zohoStatus || null,
  render: (_, record) => {
    const isEditing = editingStatus.rowId === record._id && editingStatus.field === 'zohoStatus';
    const latestStatusInfo = getLatestStatusWithDate(record);
    const status = latestStatusInfo.status;
    const sentStatusEntry = getLatestZohoStatusEntry(record, ['SENT']);
    const terminalStatusEntry = getLatestZohoStatusEntry(record, ['RECEIVED', 'COMPLETED']);
    
    // Get the oldest entry for current status (first occurrence)
    const getFirstStatusDate = () => {
      const history = record.zohoStatusHistory || [];
      const statusEntries = history.filter(entry => entry.status === status);
      if (statusEntries.length === 0) return null;
      const sortedEntries = [...statusEntries].sort((a, b) => new Date(a.changedAt) - new Date(b.changedAt));
      return sortedEntries[0]?.changedAt;
    };
    
    const firstStatusDate = getFirstStatusDate();

    const getStatusColor = (status) => {
      const colors = {
        'NOT-SENT': 'default',
        'SENT': 'processing',
        'RECEIVED': 'blue',
        'COMPLETED': 'green',
        'INCOMPLETE': 'warning',
        'FAILED': 'error',
      };
      return colors[status] || 'default';
    };

    if (isEditing) {
      return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Select
            value={editingStatus.value}
            onChange={(val) => {
              if (!hasDocServiceAgentAssigned(record)) {
                setEditingStatus(prev => ({ ...prev, value: status }));
                showMissingDocServiceAgentError();
                return;
              }

              setEditingStatus(prev => ({ ...prev, value: val }));
            }}
            style={{ width: '100%' }}
            size="small"
            disabled={editingStatus.loading}
          >
            {['NOT-SENT', 'SENT', 'COMPLETED', 'INCOMPLETE', 'FAILED'].map(opt => (
              <Option key={opt} value={opt}>{opt}</Option>
            ))}
          </Select>
          <DatePicker
            value={editingDate}
            onChange={setEditingDate}
            format="MM/DD/YYYY"
            size="small"
            placeholder="Select date"
            style={{ width: '100%' }}
          />
          <Space size="small">
            <Button
              size="small"
              type="primary"
              onClick={async () => {
                if (!hasDocServiceAgentAssigned(record)) {
                  setEditingStatus(prev => ({ ...prev, value: status, loading: false }));
                  showMissingDocServiceAgentError();
                  return;
                }

                if (!editingDate) {
                  message.warning('Please select a date');
                  return;
                }
                setEditingStatus(prev => ({ ...prev, loading: true }));
                const success = await updateFieldWithHistory(
                  record, 
                  'zohoStatus', 
                  editingStatus.value,
                  'zohoStatusHistory',
                  'zohoStatusUpdatedAt',
                  editingDate.toISOString()
                );
                if (success) {
                  setEditingStatus({ rowId: null, field: null, value: '', loading: false });
                  setEditingDate(null);
                } else {
                  setEditingStatus(prev => ({ ...prev, loading: false }));
                }
              }}
              loading={editingStatus.loading}
            >
              Save
            </Button>
            <Button 
              size="small" 
              onClick={() => {
                setEditingStatus({ rowId: null, field: null, value: '', loading: false });
                setEditingDate(null);
              }}
            >
              Cancel
            </Button>
          </Space>
        </Space>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Tag
            color={getStatusColor(status)}
            style={{ minWidth: 96, textAlign: 'center', marginInlineEnd: 0 }}
          >
            {status}
          </Tag>
          <Tooltip title="Edit Status with Date">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                if (!hasDocServiceAgentAssigned(record)) {
                  showMissingDocServiceAgentError();
                  return;
                }

                // Get the existing date for this status
                const existingDate = firstStatusDate || new Date();
                setEditingDate(dayjs(existingDate));
                setEditingStatus({
                  rowId: record._id,
                  field: 'zohoStatus',
                  value: status,
                  loading: false,
                });
              }}
              style={{ color: '#1890ff', paddingInline: 4 }}
            />
          </Tooltip>
        </div>
        
        {/* Show the oldest date for this status */}
        {firstStatusDate && (
          <div style={{ marginTop: 4 }}>
            <Tooltip title={`Changed on ${dayjs(firstStatusDate).format('MM/DD/YYYY hh:mm A')}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {dayjs(firstStatusDate).format('MM/DD/YY hh:mm A')}
                </Text>
              </div>
            </Tooltip>
          </div>
        )}
        
      
     
      </div>
    );
  },
},
    {
      title: 'Payment Info',
      key: 'dateInfo',
      width: 236,
      sorter: (a, b) => {
        const dateA = getLastPaymentDate(a);
        const dateB = getLastPaymentDate(b);
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateB) - new Date(dateA);
      },
      render: (_, record) => {
        const lastPaymentDate = getLastPaymentDate(record);
        const latestPaymentAllowEntry = getLatestPaymentAllowEntry(record);
        const paymentAllowValue = latestPaymentAllowEntry?.value || 'N/A';
        const paymentAllowBadgeStyle =
          paymentAllowValue === 'Yes'
            ? {
                color: '#389e0d',
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
              }
            : paymentAllowValue === 'No'
              ? {
                  color: '#cf1322',
                  background: '#fff1f0',
                  border: '1px solid #ffa39e',
                }
              : {
                  color: '#595959',
                  background: '#fafafa',
                  border: '1px solid #d9d9d9',
                };
        const paymentDateText = lastPaymentDate
          ? `${dayjs(lastPaymentDate).format('MM/DD/YY')} \u00B7 ${dayjs(lastPaymentDate).format('hh:mm A')}`
          : 'N/A';
        const paymentRelativeText = lastPaymentDate
          ? `Last Payment Date \u00B7 ${dayjs(lastPaymentDate).fromNow()}`
          : 'Last Payment Date';

        return (
          <Tooltip
            title={
              <div>
                <strong>Last Payment Date:</strong>{' '}
                {lastPaymentDate ? dayjs(lastPaymentDate).format('MM-DD-YYYY hh:mm A') : 'N/A'}
                {latestPaymentAllowEntry?.changedAt ? (
                  <div>
                    <strong>payementAllow Updated:</strong>{' '}
                    {dayjs(latestPaymentAllowEntry.changedAt).format('MM-DD-YYYY hh:mm A')}
                  </div>
                ) : null}
              </div>
            }
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', minWidth: 0 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: '#F1EFE8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <ClockCircleOutlined style={{ fontSize: 13, color: '#888780' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, width: '100%' }}>
                <Text
                  style={{
                    display: 'block',
                    minWidth: 0,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {paymentDateText}
                </Text>
                <Text
                  type="secondary"
                  style={{
                    display: 'block',
                    minWidth: 0,
                    fontSize: 11,
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {paymentRelativeText}
                </Text>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    alignSelf: 'flex-start',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    padding: '1px 8px',
                    borderRadius: 999,
                    fontSize: 10,
                    lineHeight: 1.6,
                    fontWeight: 500,
                    ...paymentAllowBadgeStyle,
                  }}
                >
                  payementAllow: {paymentAllowValue}
                </span>
                {/*
                <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                  {lastPaymentDate ? dayjs(lastPaymentDate).format('MM/DD/YY · hh:mm A') : 'N/A'}
                </Text>
                <Text style={{ fontSize: 10, lineHeight: 1.2, color: paymentAllowColor }}>
                  payementAllow: {paymentAllowValue}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                  Last Payment Date{lastPaymentDate ? ` · ${dayjs(lastPaymentDate).fromNow()}` : ''}
                </Text>
                */}
              </div>
            </div>
          </Tooltip>
        );

        return (
        <Tooltip
          title={
            <div>
              <strong>Last Payment Date:</strong>{' '}
              {lastPaymentDate ? dayjs(lastPaymentDate).format('MM-DD-YYYY hh:mm A') : 'N/A'}
            </div>
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Created */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: '#F1EFE8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <ClockCircleOutlined style={{ fontSize: 13, color: '#888780' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                  {record.createdAt ? dayjs(record.createdAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                  Created · {record.createdAt ? dayjs(record.createdAt).fromNow() : '—'}
                </Text>
              </div>
            </div>

            {/* Updated */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: '#E6F1FB',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <SyncOutlined style={{ fontSize: 13, color: '#185FA5' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: '#185FA5' }}>
                  {record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                  Updated · {record.updatedAt ? dayjs(record.updatedAt).fromNow() : '—'}
                </Text>
              </div>
            </div>
          </div>
        </Tooltip>
        );
      },
    },
  {
      title: 'Comment',
      key: 'zohoPageComment',
      width: 280,
      render: (_, record) => {
          const comments = record.zohoPageComment || [];
          const latestComment = comments.length > 0 ? comments[comments.length - 1] : null;
          
          return (
              <div>
               
  
                  {/* Display Latest Comment */}
                  {latestComment ? (
                      <div 
                          style={{
                              background: '#fafafa',
                              padding: '8px 10px',
                              borderRadius: '8px',
                              border: '1px solid #f0f0f0',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              wordBreak: 'break-word',      // Add this
                              wordWrap: 'break-word',       // Add this
                              overflowWrap: 'break-word',   // Add this
                              whiteSpace: 'normal'          // Add this
                          }}
                          onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLeadForComment(record);
                              setCommentsModalVisible(true);
                          }}
                      >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <Text type="secondary" style={{ fontSize: '10px' }}>
                                  <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                  {formatCommentDate(latestComment.changedAt)}
                              </Text>
                              {comments.length > 1 && (
                                  <Tag 
                                      color="blue" 
                                      style={{ 
                                          fontSize: '10px', 
                                          padding: '0 4px', 
                                          margin: 0,
                                          cursor: 'pointer'
                                      }}
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedLeadForComment(record);
                                          setCommentsModalVisible(true);
                                      }}
                                  >
                                      +{comments.length - 1} more
                                  </Tag>
                              )}
                          </div>
                          <Text style={{ 
                              fontSize: '12px', 
                              display: 'block', 
                              wordBreak: 'break-word',    // Add this
                              whiteSpace: 'pre-wrap'       // Add this for preserving whitespace and wrapping
                          }}>
                              {latestComment.comment.length > 80 
                                  ? `${latestComment.comment.substring(0, 80)}...` 
                                  : latestComment.comment}
                          </Text>
                      </div>
                  ) : (
                      <div 
                          style={{ 
                              textAlign: 'center', 
                              padding: '12px',
                              background: '#fafafa',
                              borderRadius: '8px',
                              border: '1px solid #f0f0f0',
                              cursor: 'pointer'
                          }}
                          onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLeadForComment(record);
                              setCommentsModalVisible(true);
                          }}
                      >
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                              <CommentOutlined style={{ marginRight: '4px', fontSize: '14px' }} />
                              No comments yet
                          </Text>
                      </div>
                  )}
                     <Button
                      type="primary"
                      size="small"
                      icon={<CommentOutlined />}
                      onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLeadForComment(record);
                          setNewComment('');
                          setCommentsModalVisible(true);
                      }}
                      style={{ 
                          marginBottom: '12px',
                          width: '100%',
                          fontSize: '12px'
                      }}
                  >
                      Add Comment
                  </Button>
              </div>
          );
      },
  },
    {
      title: 'Actions',
      key: 'actions',
      width: 92,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Tooltip title="View Details">
            <Button
              type="primary"
              shape="circle"
              icon={drawerLoading && selectedLeadId === record._id ? <LoadingOutlined /> : <EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
              disabled={drawerLoading}
              style={{ backgroundColor: '#1890ff' }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="min-vh-100 p-4" style={{ 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <style jsx>{`
        .custom-zoho-table .ant-table-thead > tr > th {
          background: #f8f9fa;
          font-weight: 600;
          font-size: 14px;
          color: #1f2937;
          padding: 12px 12px;
          border-bottom: 1px solid #e9ecef;
        }

        .custom-zoho-table .ant-table-tbody > tr:hover > td {
          background: #f5f9ff !important;
        }

        .custom-zoho-table .ant-table-tbody > tr > td {
          padding: 12px 12px;
          vertical-align: top;
        }

        .custom-zoho-table .ant-table-tbody > tr > td.ant-table-cell-fix-right {
          vertical-align: middle;
        }

        .custom-zoho-table .table-row-light {
          background-color: #ffffff;
        }

        .custom-zoho-table .table-row-dark {
          background-color: #fafbfc;
        }

        .custom-zoho-table .ant-pagination {
          margin: 16px 24px;
        }

        .custom-zoho-table .ant-table-expanded-row-fixed {
          background: #f8f9fa;
          border-radius: 8px;
          margin: 8px 0;
        }
      
        .glass-card {
          background: white;
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
          overflow: hidden;
        }
        
        .header-section {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          background: white;
          border-bottom: 1px solid #f0f0f0;
        }

        .logo-img {
          height: 45px;
          width: auto;
          object-fit: contain;
        }

        .filter-section {
          padding: 16px 24px;
          background: #fafafa;
          border-bottom: 1px solid #f0f0f0;
        }

        .search-section {
          padding: 16px 24px;
          background: white;
          border-bottom: 1px solid #f0f0f0;
        }

        .ant-table-wrapper {
          background: white;
        }

        .ant-table-thead > tr > th {
          background: #f8f9fa;
          font-weight: 600;
          color: #1f2937;
          white-space: nowrap;
          padding: 12px 16px;
        }

        .ant-table-tbody > tr:hover > td {
          background: #f5f9ff !important;
        }

        .ant-table-pagination {
          margin: 16px 24px !important;
        }

        .ant-tag {
          border-radius: 4px;
          padding: 2px 8px;
          font-size: 12px;
          line-height: 18px;
        }

        .drawer-loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255,255,255,0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .date-filter-select {
          min-width: 150px;
        }

        .custom-range-picker {
          min-width: 250px;
        }
      `}</style>

      <div className="container-fluid px-0">
        <div className="glass-card">
          {/* Header with Logo */}
          <div className="header-section">
            <div className="d-flex align-items-center">
              <div>
                <Title level={4} style={{ margin: 0, color: '#1f2937' }}>
                  Pending Zoho Sign Documents
                </Title>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  View and manage leads with pending document status (SENT, NOT-SENT, INCOMPLETE)
                </Text>
              </div>
            </div>
            <img src={Logo} alt="Logo" className="logo-img" />
          </div>

          <div className="filter-section" style={{ padding: '20px 24px', background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
            <Row gutter={[24, 16]} align="middle">
              {/* Left group: Date filters */}
              <Col xs={24} md={16} lg={12}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isAdmin && (
                    <div style={{ minWidth: 180 }}>
                      <Select
                        placeholder="Filter by Agent"
                        value={selectedAgentId}
                        onChange={setSelectedAgentId}
                        style={{ width: '100%' }}
                        size="middle"
                        allowClear
                        suffixIcon={<TeamOutlined />}
                        showSearch
                        optionFilterProp="children"
                      >
                        {agents.map(agent => (
                          <Option key={agent._id} value={agent._id}>
                            <Space>
                              <Avatar size="small" icon={<UserOutlined />} />
                              <span>{agent.firstName} {agent.lastName}</span>
                            </Space>
                          </Option>
                        ))}
                      </Select>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalendarOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                    <Select
                      value={dateFilterType}
                      style={{ width: 140 }}
                      size="middle"
                      onChange={handleDateFilterChange}
                      dropdownMatchSelectWidth={false}
                    >
                      {dateFilterOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <RangePicker
                      value={customDateRange}
                      onChange={(dates) => {
                        setDateFilterType("Custom");
                        setCustomDateRange(dates);
                      }}
                      size="middle"
                      format="YYYY-MM-DD"
                      allowClear={false}
                      style={{ minWidth: 240 }}
                    />
                  </div>
                </div>
              </Col>

              {/* Right group: Agent filter & actions */}
              <Col xs={24} md={8} lg={12}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <Space size="small">
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={clearFilters}
                      size="middle"
                    >
                      Clear
                    </Button>
                    <Button 
                      type="primary" 
                      icon={<FilterOutlined />}
                      onClick={() => fetchLeads()}
                      size="middle"
                      style={{ background: '#667eea', borderColor: '#667eea' }}
                    >
                      Apply
                    </Button>
                  </Space>
                </div>
              </Col>
            </Row>
          </div>

          {/* Search and Filter Bar */}
          <div className="search-section">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={12} lg={16}>
                <Space.Compact block>
                  <SearchFieldsSelect value={searchFields} onChange={setSearchFields} />
                  <Input
                    placeholder={getSearchFieldsPlaceholder(searchFields)}
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    size="large"
                  />
                </Space.Compact>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <div className="d-flex justify-content-end gap-2">
                  <Text type="secondary">
                    Total Leads: {pagination.total}
                  </Text>
                </div>
              </Col>
            </Row>
          </div>

          {/* Table */}
          <Table
            columns={enhancedColumns}
            dataSource={leads}
            rowKey="_id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              position: ['topRight'], 
              showTotal: (total, range) => (
                <Text type="secondary">
                  Showing {range[0]}-{range[1]} of {total} leads
                </Text>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
              size: 'default',
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content', y: 'calc(100vh - 480px)' }}
            tableLayout="fixed"
            bordered={false}
            size="middle"
            sticky={{ offsetHeader: 0 }}
            rowClassName={(record, index) => 
              index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
            }
            className="custom-zoho-table"
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: '12px 24px', background: '#fafafa' }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Text strong>Additional Details</Text>
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">Lead Type: </Text>
                        <Tag>{record.leadType || 'N/A'}</Tag>
                      </div>
                      <div>
                        <Text type="secondary">Source: </Text>
                        <Text>{record.source || 'N/A'}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Notes: </Text>
                        <Text ellipsis={{ tooltip: true }}>{record.notes || 'No notes'}</Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Text strong>Payment Info</Text>
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">Total: </Text>
                        <Text strong>${record.totalAmount?.toLocaleString() || '0'}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Paid: </Text>
                        <Text>${record.paidAmount?.toLocaleString() || '0'}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Balance: </Text>
                        <Text type={record.balanceAmount > 0 ? 'danger' : 'success'}>
                          ${record.balanceAmount?.toLocaleString() || '0'}
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>
              ),
              rowExpandable: (record) => true,
            }}
          />

          {/* Footer with count */}
          <div className="bg-light p-3 text-end border-top">
            <Text type="secondary">
              Total Leads: {pagination.total}
            </Text>
          </div>
        </div>
      </div>

      {/* Drawer Component with fetched data */}
      <LeadDetailsDrawer
        visible={drawerVisible}
        onClose={onDrawerClose}
        leadData={selectedLeadData}
        onLeadUpdate={handleLeadUpdate}
        loading={drawerLoading}
      />

         <Modal
   
    open={commentsModalVisible}
    onCancel={() => {
        setCommentsModalVisible(false);
        setSelectedLeadForComment(null);
        setNewComment('');
    }}
    footer={[
        <Button key="cancel" onClick={() => {
            setCommentsModalVisible(false);
            setSelectedLeadForComment(null);
            setNewComment('');
        }}>
            Cancel
        </Button>,
        <Button 
            key="submit" 
            type="primary" 
            onClick={handleAddComment}
            loading={addingComment}
            disabled={!newComment.trim()}
        >
            Add Comment
        </Button>
    ]}
    width={500}
>
    <div style={{ padding: '16px 0' }}>
      

        <div>
            <Text strong>Comment:</Text>
            <Input.TextArea
                placeholder="Enter your comment here..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={4}
                maxLength={1000}
                showCount
                style={{ marginTop: '8px' }}
                autoFocus
            />
        </div>

        {/* Show existing comments if any */}
        {selectedLeadForComment?.zohoPageComment?.length > 0 && (
            <div style={{ marginTop: '20px' }}>
                <Divider />
                <Text strong>Previous Comments ({selectedLeadForComment.zohoPageComment.length})</Text>
                <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    marginTop: '8px',
                    padding: '8px',
                    background: '#f5f5f5',
                    borderRadius: '8px'
                }}>
                    {[...selectedLeadForComment.zohoPageComment]
                        .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                        .map((comment, idx) => (
                            <div key={idx} style={{ 
                                marginBottom: '12px', 
                                padding: '8px',
                                background: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e8e8e8'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                        {formatCommentDate(comment.changedAt)}
                                    </Text>
                                </div>
                                <Text style={{ fontSize: '13px' }}>{comment.comment}</Text>
                            </div>
                        ))}
                </div>
            </div>
        )}
    </div>
</Modal>
{/* Service Agent Assignment Modal */}
<Modal
    title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <TeamOutlined style={{ fontSize: 20, color: 'white' }} />
            </div>
            <div>
                <Title level={4} style={{ margin: 0 }}>Assign Service Agent</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Select service agent for this lead
                </Text>
            </div>
        </div>
    }
    open={serviceAgentModalVisible}
    onCancel={() => {
        setServiceAgentModalVisible(false);
        setSelectedLeadForServiceAgent(null);
        setSelectedServiceAgentForLead(null);
    }}
    footer={null}
    width={500}
    centered
>
    <div style={{ padding: '16px 0' }}>
        {selectedLeadForServiceAgent && (
            <>
                {/* Customer Info Card */}
                <Card
                    size="small" 
                    style={{ 
                        marginBottom: 20,
                        background: '#f5f5f5',
                        borderRadius: 12,
                        border: 'none'
                    }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text strong>
                            {selectedLeadForServiceAgent.personalInfo?.fullName || 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {selectedLeadForServiceAgent.vehicleInfo?.make} {selectedLeadForServiceAgent.vehicleInfo?.model} - {selectedLeadForServiceAgent.vehicleInfo?.part}
                        </Text>
                        {selectedLeadForServiceAgent.zohoServiceHandledBy && (
                            <div style={{ 
                                marginTop: 8,
                                paddingTop: 8,
                                borderTop: '1px solid #e8e8e8'
                            }}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Currently assigned: 
                                </Text>
                                <Text strong style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                                    {getServiceAgentName(selectedLeadForServiceAgent)}
                                </Text>
                            </div>
                        )}
                    </Space>
                </Card>

                {/* Service Agent Selection */}
                <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Select Service Agent:
                    </Text>
                    <Select
                        value={selectedServiceAgentForLead}
                        onChange={setSelectedServiceAgentForLead}
                        style={{ width: '100%' }}
                        size="large"
                        placeholder="Choose an agent"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        loading={loadingServiceAgents}
                    >
                        {Object.entries(serviceAgentsMap).map(([agentId, agentInfo]) => (
                            <Option key={agentId} value={agentId}>
                                <Space direction="vertical" size={0}>
                                    <Text strong>{agentInfo.name}</Text>
                                   
                                </Space>
                            </Option>
                        ))}
                    </Select>
                    <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                        {selectedServiceAgentForLead ? 'Agent will be assigned to this lead' : 'Clear to unassign the current agent'}
                    </Text>
                </div>

                {/* Modal Actions */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <Button 
                        onClick={() => {
                            setServiceAgentModalVisible(false);
                            setSelectedLeadForServiceAgent(null);
                            setSelectedServiceAgentForLead(null);
                        }} 
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleServiceAgentSave}
                        loading={savingServiceAgent}
                        size="large"
                        icon={<CheckOutlined />}
                    >
                        {selectedServiceAgentForLead ? 'Assign Agent' : 'Unassign Agent'}
                    </Button>
                </div>
            </>
        )}
    </div>
</Modal>
    </div>
  );
};

export default ZohoSign;


